import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ImageUploader } from '../../components/ImageUploader';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export const AdminProducts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', category_id: '', stock: '10', sku: '', images: [] });

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    const [p, c] = await Promise.all([api.get('/products?limit=200'), api.get('/categories')]);
    setProducts(p.data.products);
    setCategories(c.data);
    setLoading(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', price: '', category_id: '', stock: '10', sku: '', images: [] });
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, price: p.price, category_id: p.category_id, stock: p.stock, sku: p.sku, images: p.images || [] });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category_id || !form.sku) { toast.error('Fill required fields'); return; }
    const data = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock), description: '' };
    if (data.images.length === 0) data.images = ['https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=400'];
    try {
      if (editing) await api.put('/admin/products/' + editing.id, data);
      else await api.post('/admin/products', data);
      toast.success(editing ? 'Updated' : 'Created');
      setShowDialog(false);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete('/admin/products/' + id);
    fetchData();
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin')}><ArrowLeft size={24} /></button>
          <h1 className="text-2xl font-bold">Products</h1>
        </div>
        <Button onClick={openAdd} className="bg-orange-600"><Plus size={18} className="mr-2" />Add</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600" /></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs">Image</th>
                <th className="px-4 py-3 text-left text-xs">Product</th>
                <th className="px-4 py-3 text-left text-xs">Price</th>
                <th className="px-4 py-3 text-left text-xs">Stock</th>
                <th className="px-4 py-3 text-left text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <ProductRow key={p.id} p={p} onEdit={openEdit} onDelete={del} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Product</DialogTitle></DialogHeader>
          <ProductForm form={form} setForm={setForm} categories={categories} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-orange-600">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProductRow = ({ p, onEdit, onDelete }) => (
  <tr className="border-b">
    <td className="px-4 py-3">
      <img src={p.images?.[0] || 'https://via.placeholder.com/50'} className="w-12 h-12 rounded object-cover" alt="" />
    </td>
    <td className="px-4 py-3">
      <p className="font-medium">{p.name}</p>
      <p className="text-xs text-gray-500">{p.category_name}</p>
    </td>
    <td className="px-4 py-3">₹{p.price}</td>
    <td className="px-4 py-3">{p.stock}</td>
    <td className="px-4 py-3">
      <button onClick={() => onEdit(p)} className="p-1 text-blue-600"><Edit size={18} /></button>
      <button onClick={() => onDelete(p.id)} className="p-1 text-red-600"><Trash2 size={18} /></button>
    </td>
  </tr>
);

const ProductForm = ({ form, setForm, categories }) => (
  <div className="grid gap-3 py-2">
    <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
    <div className="grid grid-cols-2 gap-3">
      <div><Label>SKU *</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
      <div><Label>Price *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
      <div>
        <Label>Category *</Label>
        <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <div>
      <Label>Images</Label>
      <ImageUploader images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} />
    </div>
  </div>
);
